import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import * as XLSX from 'xlsx';
import { API_config } from '../../config/apiConfig';
import 'leaflet/dist/leaflet.css';
import './SafetyPointsAdmin.styles.css';

// Fix generic leaflet icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom Icons for different types
const getIconForType = (type, sequence) => {
    let color = '#9ca3af'; // grey default (Other)
    if (type === 'Safety') color = '#f97316'; // orange
    if (type === 'Marshal') color = '#ef4444'; // red
    if (type === 'Police') color = '#3b82f6'; // blue
    if (type === 'Other') color = '#9ca3af'; // grey
    
    const text = sequence != null ? sequence : '';
    
    return L.divIcon({
        className: 'custom-safety-marker',
        html: `<div style="background-color: ${color}; width: 22px; height: 22px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 11px;">${text}</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
    });
};

const MapEvents = ({ onMapClick }) => {
    useMapEvents({
        click(e) {
            onMapClick(e.latlng);
        },
    });
    return null;
};

// Map component to handle zoom fitting
const MapFitter = ({ bounds }) => {
    const map = useMap();
    useEffect(() => {
        if (bounds && bounds.length > 0) {
            map.fitBounds(bounds, { padding: [20, 20] });
        }
    }, [bounds, map]);
    return null;
};

const SafetyPointsAdmin = () => {
    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({});
    
    // Stage integration states
    const [availableStages, setAvailableStages] = useState([]);
    const [selectedStageId, setSelectedStageId] = useState('');
    const [stageTrack, setStageTrack] = useState(null);
    const [stageBounds, setStageBounds] = useState(null);
    const [importType, setImportType] = useState('Safety');
    
    // Auth token
    const token = localStorage.getItem('token') || API_config.bearerToken;
    
    const apiUrl = API_config.safetyPointsApiUrl;
    const dataApiUrl = API_config.dataApiUrl;

    useEffect(() => {
        fetchPoints();
        fetchAvailableStages();
    }, []);

    // Fetch individual stage details when selected
    useEffect(() => {
        if (!selectedStageId) {
            setStageTrack(null);
            setStageBounds(null);
            return;
        }

        const fetchStageTrack = async () => {
            try {
                const res = await fetch(`${dataApiUrl}?type=stage&id=${selectedStageId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.coords) {
                        // stages.php returns coords based on coordOrder ('lonlat' or latlon)
                        const coordOrder = data.coordOrder || 'lonlat';
                        const latLngs = data.coords.map(c => coordOrder === 'lonlat' ? [c[1], c[0]] : [c[0], c[1]]);
                        setStageTrack(latLngs);
                        
                        // Set bounds to auto-zoom map
                        if (latLngs.length > 0) {
                            const bounds = L.latLngBounds(latLngs);
                            setStageBounds([
                                [bounds.getSouth(), bounds.getWest()],
                                [bounds.getNorth(), bounds.getEast()]
                            ]);
                        }
                    } else {
                        setStageTrack(null);
                    }
                }
            } catch (err) {
                console.error("Error fetching stage track:", err);
                setStageTrack(null);
            }
        };

        fetchStageTrack();
    }, [selectedStageId]);

    const fetchAvailableStages = async () => {
        try {
            const res = await fetch(`${dataApiUrl}?type=all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                let flattened = [];
                if (data && data.sections) {
                    data.sections.forEach(sec => {
                        if (sec.os) flattened = [...flattened, ...sec.os];
                        if (sec.liaisons) flattened = [...flattened, ...sec.liaisons];
                    });
                }
                setAvailableStages(flattened);
            }
        } catch (error) {
            console.error('Error fetching available stages:', error);
        }
    };

    const fetchPoints = async () => {
        setLoading(true);
        try {
            const separator = apiUrl.includes('?') ? '&' : '?';
            const res = await fetch(`${apiUrl}${separator}_t=${Date.now()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                cache: 'no-store'
            });
            if (res.ok) {
                const data = await res.json();
                setPoints(data || []);
            }
        } catch (error) {
            console.error('Error fetching safety points:', error);
        } finally {
            setLoading(false);
        }
    };

    const getSelectedStageName = () => {
        if (!selectedStageId) return '';
        const s = availableStages.find(x => x.id.toString() === selectedStageId.toString());
        return s ? s.name : '';
    };

    const handleKmlUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!selectedStageId) {
            alert('Please select a stage from the dropdown first to import points to!');
            e.target.value = null; // reset input
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('action', 'import');
        formData.append('kml_file', file);
        formData.append('stage_id', selectedStageId);
        formData.append('stage_name', getSelectedStageName());
        formData.append('type', importType);

        try {
            const res = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                alert(data.message);
                fetchPoints();
            } else {
                alert('Warning/Error: ' + data.error);
            }
        } catch (error) {
            console.error('Upload failed', error);
            alert('Upload failed');
        } finally {
            setUploading(false);
            e.target.value = null; // reset input
        }
    };

    const handleMapClick = async (latlng) => {
        if (!window.confirm('Add new safety point here?')) return;
        
        const formData = new URLSearchParams();
        formData.append('action', 'add');
        formData.append('name', 'New Point');
        formData.append('latitude', latlng.lat);
        formData.append('longitude', latlng.lng);
        formData.append('type', importType);
        
        if (selectedStageId) {
            formData.append('stage_id', selectedStageId);
            formData.append('stage_name', getSelectedStageName());
        }

        try {
            const res = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData
            });
            if (res.ok) fetchPoints();
        } catch (error) {
            console.error('Add failed', error);
        }
    };

    const handleDragEnd = async (e, id) => {
        const marker = e.target;
        const position = marker.getLatLng();
        
        const formData = new URLSearchParams();
        formData.append('action', 'update');
        formData.append('id', id);
        formData.append('latitude', position.lat);
        formData.append('longitude', position.lng);
        
        try {
            await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData
            });
            // Update local state without refetching all points for smoothness
            setPoints(points.map(p => p.id === id ? { ...p, latitude: position.lat, longitude: position.lng } : p));
        } catch (error) {
            console.error('Update coordinates failed', error);
            fetchPoints(); // revert on fail
        }
    };

    const openEditModal = (point) => {
        setEditForm({ ...point });
        setIsEditModalOpen(true);
    };

    const handleEditFormChange = (e) => {
        setEditForm({ ...editForm, [e.target.name]: e.target.value });
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        const formData = new URLSearchParams();
        formData.append('action', 'update');
        formData.append('id', editForm.id);
        formData.append('name', editForm.name || '');
        formData.append('description', editForm.description || '');
        formData.append('contact_phone', editForm.contact_phone || '');
        const validTypes = ['Safety', 'Marshal', 'Police', 'Other'];
        formData.append('type', validTypes.includes(editForm.type) ? editForm.type : 'Other');
        formData.append('position_sequence', editForm.position_sequence || '');
        if (editForm.stage_id) {
             formData.append('stage_id', editForm.stage_id);
             // assume stage name stays same or can be recalculated, simplifying:
             const sName = availableStages.find(x => x.id.toString() === editForm.stage_id.toString())?.name;
             if (sName) formData.append('stage_name', sName);
        }

        try {
            const res = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData
            });
            const responseData = await res.json();
            if (responseData.success !== false) {
                setIsEditModalOpen(false);
                fetchPoints();
            } else {
                alert('Edit Failed: ' + (responseData.error || 'Update rejected by server.'));
                console.error(responseData);
            }
        } catch (error) {
            console.error('Edit failed', error);
            alert('Edit Failed: Network or Server Error');
        }
    };

    const deletePoint = async (id) => {
        if (!window.confirm('Delete this point?')) return;
        
        const formData = new URLSearchParams();
        formData.append('action', 'delete');
        formData.append('id', id);
        
        try {
            await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData
            });
            fetchPoints();
        } catch (error) {
            console.error('Delete failed', error);
        }
    };

    const handleDeleteAll = async () => {
        const confirmMsg = selectedStageId 
            ? `Are you sure you want to delete ALL points matching the selected stage (${getSelectedStageName()})?` 
            : `Are you sure you want to delete ALL points in the system?`;
            
        if (!window.confirm(confirmMsg)) return;

        const formData = new URLSearchParams();
        formData.append('action', 'delete_all');
        if (selectedStageId) {
            formData.append('stage_id', selectedStageId);
        }

        try {
            await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formData
            });
            fetchPoints();
        } catch (error) {
            console.error('Delete all failed', error);
        }
    };

    const handleDownloadExcel = () => {
        let exportPoints = points;
        if (selectedStageId) {
            exportPoints = points.filter(p => p.stage_id && p.stage_id.toString() === selectedStageId.toString());
        } else {
            if (!window.confirm("No stage selected. Download ALL points in the system?")) return;
        }

        if (exportPoints.length === 0) {
            alert('No points to export for this selection.');
            return;
        }

        const dataRows = exportPoints.map(p => ({
            ID: p.id,
            Name: p.name,
            Description: p.description || '',
            Sequence: p.position_sequence || '',
            Phone: p.contact_phone || '',
            Type: p.type || 'Other'
        }));

        const ws = XLSX.utils.json_to_sheet(dataRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "SafetyPoints");
        XLSX.writeFile(wb, `SafetyPoints_${selectedStageId ? getSelectedStageName() : 'All'}.xlsx`);
    };

    const handleUploadExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const reader = new FileReader();

        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (!data || data.length === 0 || !data[0].hasOwnProperty('ID')) {
                    alert('Invalid Excel file. Ensure the header contains "ID".');
                    setUploading(false);
                    return;
                }

                const formData = new URLSearchParams();
                formData.append('action', 'import_excel');
                formData.append('data', JSON.stringify(data));

                const res = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    body: formData
                });
                const responseData = await res.json();
                if (responseData.success) {
                    alert(responseData.message);
                    fetchPoints();
                } else {
                    alert('Error: ' + responseData.error);
                }

            } catch (err) {
                console.error("Excel Read Error", err);
                alert("Failed to process Excel file.");
            } finally {
                setUploading(false);
                e.target.value = null;
            }
        };
        reader.readAsBinaryString(file);
    };

    return (
        <div className="safety-points-admin">
            <div className="safety-points-header">
                <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                    <h2>Safety Points Management</h2>
                    
                    {availableStages.length > 0 && (
                        <select 
                            className="stage-select-dropdown" 
                            style={{padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)'}}
                            value={selectedStageId} 
                            onChange={(e) => setSelectedStageId(e.target.value)}
                        >
                            <option value="">-- No Stage Selected --</option>
                            {availableStages.map(stage => (
                                <option key={stage.id} value={stage.id}>
                                    {stage.name} ({stage.kind || 'Stage'})
                                </option>
                            ))}
                        </select>
                    )}
                </div>
                
                <div className="safety-points-actions">
                    <select 
                        style={{padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)'}}
                        value={importType} 
                        onChange={(e) => setImportType(e.target.value)}
                        title="Set the default type for imported or clicked points"
                    >
                        <option value="Safety">Safety</option>
                        <option value="Marshal">Marshal</option>
                        <option value="Police">Police</option>
                        <option value="Other">Other</option>
                    </select>

                    <button 
                        className="btn-delete" 
                        onClick={handleDeleteAll}
                        style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                    >
                        <span className="material-icons-outlined" style={{ fontSize: '18px' }}>delete_sweep</span>
                        {selectedStageId ? 'Clear Stage' : 'Clear All'}
                    </button>
                    <label className="btn-upload" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span className="material-icons-outlined" style={{ fontSize: '18px' }}>upload_file</span>
                        {uploading ? 'Wait...' : 'Import KML'}
                        <input type="file" accept=".kml" onChange={handleKmlUpload} disabled={uploading} style={{ display: 'none' }} />
                    </label>
                    <button className="btn-refresh" onClick={fetchPoints} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span className="material-icons-outlined">refresh</span>
                    </button>
                </div>
            </div>

            <div className="safety-points-content">
                <div className="safety-points-map-container">
                    <MapContainer 
                        center={[51.505, -0.09]} 
                        zoom={5} 
                        style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; OpenStreetMap contributors'
                        />
                        <MapEvents onMapClick={handleMapClick} />
                        {stageBounds && <MapFitter bounds={stageBounds} />}
                        
                        {stageTrack && <Polyline positions={stageTrack} color="var(--accent-primary, blue)" weight={4} opacity={0.6} />}

                        {points.map((point) => {
                            // Optionally filter points if a stage is selected (but we show all for now)
                            /* if (selectedStageId && point.stage_id?.toString() !== selectedStageId.toString()) return null; */
                            return (
                                <Marker
                                    key={point.id}
                                    position={[point.latitude, point.longitude]}
                                    draggable={true}
                                    icon={getIconForType(point.type, point.position_sequence)}
                                    eventHandlers={{
                                        dragend: (e) => handleDragEnd(e, point.id),
                                        click: () => openEditModal(point)
                                    }}
                                >
                                    <Popup>
                                        <strong>{point.name}</strong><br />
                                        {point.stage_name && <span style={{fontSize:'11px', color:'gray'}}>Stage: {point.stage_name}<br/></span>}
                                        {point.contact_phone && <span>📞 {point.contact_phone}<br /></span>}
                                        <span style={{textTransform: 'capitalize'}}>{point.type}</span><br />
                                        <button onClick={() => deletePoint(point.id)} className="btn-delete-small">Delete</button>
                                    </Popup>
                                </Marker>
                            );
                        })}
                    </MapContainer>
                </div>

                <div className="safety-points-list">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                        <h3 style={{margin: 0}}>Points List ({points.length})</h3>
                        <div style={{display: 'flex', gap: '0.5rem'}}>
                            <button className="btn-save" onClick={handleDownloadExcel} style={{padding: '0.3rem 0.6rem', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px'}}>
                                <span className="material-icons-outlined" style={{fontSize: '16px'}}>download</span> Excel
                            </button>
                            <label className="btn-upload" style={{padding: '0.3rem 0.6rem', fontSize: '12px', margin: 0, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'}}>
                                <span className="material-icons-outlined" style={{fontSize: '16px'}}>upload</span> Update
                                <input type="file" accept=".xlsx, .xls, .csv" onChange={handleUploadExcel} disabled={uploading} style={{ display: 'none' }} />
                            </label>
                        </div>
                    </div>
                    
                    <div className="points-list-scroll">
                        {points.map(p => {
                            // We can fade out unassociated points or filter them?
                            const opacity = (selectedStageId && p.stage_id?.toString() !== selectedStageId) ? 0.4 : 1;
                            
                            return (
                                <div key={p.id} className="point-list-item" onClick={() => openEditModal(p)} style={{opacity}}>
                                    <div>
                                        <strong>{p.name}</strong>
                                        {p.stage_name && <div style={{fontSize:'10px', color:'gray'}}>{p.stage_name}</div>}
                                    </div>
                                    <span>{p.type}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {isEditModalOpen && (
                <div className="safety-modal-overlay">
                    <div className="safety-modal">
                        <h3>Edit Safety Point</h3>
                        <form onSubmit={saveEdit}>
                            <div className="form-group">
                                <label>Name</label>
                                <input name="name" value={editForm.name || ''} onChange={handleEditFormChange} required />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea name="description" value={editForm.description || ''} onChange={handleEditFormChange} rows="3" style={{width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)'}} />
                            </div>
                            <div className="form-group">
                                <label>Sequence Number</label>
                                <input type="number" name="position_sequence" value={editForm.position_sequence || ''} onChange={handleEditFormChange} />
                            </div>
                            <div className="form-group">
                                <label>Stage Link</label>
                                <select name="stage_id" value={editForm.stage_id || ''} onChange={handleEditFormChange}>
                                    <option value="">-- No Stage --</option>
                                    {availableStages.map(s => <option value={s.id} key={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Phone</label>
                                <input name="contact_phone" value={editForm.contact_phone || ''} onChange={handleEditFormChange} />
                            </div>
                            <div className="form-group">
                                <label>Type</label>
                                <select name="type" value={editForm.type || 'Safety'} onChange={handleEditFormChange}>
                                    <option value="Safety">Safety</option>
                                    <option value="Marshal">Marshal</option>
                                    <option value="Police">Police</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                                <button type="button" className="btn-delete" onClick={() => { deletePoint(editForm.id); setIsEditModalOpen(false); }}>Delete</button>
                                <button type="submit" className="btn-save">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SafetyPointsAdmin;
